import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { fuzzyFilter } from '@hcd/domain'
import { Columns3, FileText, LayoutGrid, List, Plus, Search, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { NewQuoteDialog } from '@/components/quotes/new-quote-dialog'
import { QuoteCards, QuoteKanban, QuoteList } from '@/components/quotes/views'
import { NativeSelect } from '@/components/settings/form-kit'
import { PageHeader } from '@/components/shell/page'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { quotesQuery, type QuoteSummary } from '@/lib/quotes'
import { useTitle } from '@/lib/use-title'
import { cn } from '@/lib/utils'

const TABS = [
  ['all', 'All', () => true],
  ['draft', 'Drafts', (q: QuoteSummary) => q.status === 'draft'],
  [
    'approval',
    'Awaiting approval',
    (q: QuoteSummary) => q.status === 'in_review' || q.status === 'approved',
  ],
  ['client', 'With client', (q: QuoteSummary) => q.status === 'sent' || q.status === 'viewed'],
  ['accepted', 'Accepted', (q: QuoteSummary) => q.status === 'accepted'],
  [
    'closed',
    'Closed',
    (q: QuoteSummary) => ['declined', 'withdrawn', 'expired'].includes(q.status),
  ],
] as const

const VIEWS = [
  ['list', 'List', List],
  ['cards', 'Cards', LayoutGrid],
  ['kanban', 'Kanban', Columns3],
] as const
type View = (typeof VIEWS)[number][0]

const SORTS = {
  updated: [
    'Recently updated',
    (a: QuoteSummary, b: QuoteSummary) => b.updatedAt.localeCompare(a.updatedAt),
  ],
  value: [
    'Highest value',
    (a: QuoteSummary, b: QuoteSummary) => b.annualValuePence - a.annualValuePence,
  ],
  expiring: [
    'Expiring soonest',
    (a: QuoteSummary, b: QuoteSummary) =>
      (a.validUntil ?? '9999').localeCompare(b.validUntil ?? '9999'),
  ],
  reference: [
    'Reference',
    (a: QuoteSummary, b: QuoteSummary) =>
      b.reference.localeCompare(a.reference, 'en', { numeric: true }),
  ],
} as const
type SortKey = keyof typeof SORTS

const VIEW_KEY = 'hcd.quotes.view'
const storedView = (): View | undefined => {
  try {
    const v = localStorage.getItem(VIEW_KEY)
    return v === 'list' || v === 'cards' || v === 'kanban' ? v : undefined
  } catch {
    return undefined
  }
}

export const Route = createFileRoute('/_app/quotes/')({
  validateSearch: z.object({
    show: z.enum(TABS.map((t) => t[0]) as [string, ...string[]]).optional(),
    view: z.enum(['list', 'cards', 'kanban']).optional(),
    q: z.string().max(100).optional(),
    owner: z.string().max(120).optional(),
    sort: z.enum(Object.keys(SORTS) as [SortKey, ...SortKey[]]).optional(),
    new: z.boolean().optional(),
  }),
  component: QuotesPage,
})

function QuotesPage() {
  useTitle('Quotes')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const view: View = search.view ?? storedView() ?? 'list'
  const show = search.show ?? 'all'
  const sort: SortKey = search.sort ?? 'updated'
  const [creating, setCreating] = useState(!!search.new)
  const [text, setText] = useState(search.q ?? '')
  const { data, isPending, isError, error } = useQuery(quotesQuery())

  // Debounce the search box into the URL so results are shareable and survive reloads.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? '') !== text)
        navigate({ search: (s) => ({ ...s, q: text || undefined }), replace: true })
    }, 200)
    return () => clearTimeout(t)
  }, [text, search.q, navigate])

  const setSearch = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true })
  const setView = (v: View) => {
    try {
      localStorage.setItem(VIEW_KEY, v)
    } catch {
      /* private mode: fine */
    }
    setSearch({ view: v })
  }

  const all = data ?? []
  const owners = useMemo(
    () => [...new Set(all.map((q) => q.ownerName).filter(Boolean))].sort(),
    [all],
  )
  const base = useMemo(() => {
    const byOwner = search.owner ? all.filter((q) => q.ownerName === search.owner) : all
    const found = fuzzyFilter(byOwner, search.q ?? '', (q) => [
      q.reference,
      q.title,
      q.customerName,
      q.ownerName,
    ])
    return search.q ? found : [...found].sort(SORTS[sort][1])
  }, [all, search.owner, search.q, sort])
  const rows = view === 'kanban' ? base : base.filter(TABS.find((t) => t[0] === show)![2])
  const filtering = !!(search.q || search.owner)

  return (
    <>
      <PageHeader
        title="Quotes"
        actions={
          <Button className="h-10" onClick={() => setCreating(true)}>
            <Plus />
            New quote
          </Button>
        }
      />

      <div className="mb-4 grid gap-3 rounded-xl border bg-card p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_auto_auto_auto] lg:items-center">
        <div className="relative md:col-span-3 lg:col-span-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            aria-label="Search quotes"
            placeholder="Search by client, title, reference or person"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="h-11 pl-9 text-base md:h-10 md:text-[15px]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3 md:col-span-2 lg:contents">
          <NativeSelect
            aria-label="Prepared by"
            value={search.owner ?? ''}
            onChange={(e) => setSearch({ owner: e.target.value || undefined })}
            className="lg:w-44"
          >
            <option value="">Anyone</option>
            {owners.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Sort"
            value={sort}
            onChange={(e) => setSearch({ sort: e.target.value as SortKey })}
            disabled={!!search.q}
            className="lg:w-44"
          >
            {Object.entries(SORTS).map(([k, [label]]) => (
              <option key={k} value={k}>
                {search.q && k === 'updated' ? 'Best match' : label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div
          role="radiogroup"
          aria-label="View"
          className="inline-flex w-full rounded-lg border bg-muted p-1 lg:w-auto"
        >
          {VIEWS.map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={view === key}
              onClick={() => setView(key)}
              className={cn(
                'inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium lg:flex-none',
                view === key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-4" aria-hidden />
              {label}
            </button>
          ))}
        </div>
      </div>

      {view !== 'kanban' && (
        <nav aria-label="Progress" className="-mx-4 mb-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <ul className="flex w-max gap-2">
            {TABS.map(([key, label, fn]) => {
              const count = base.filter(fn).length
              return (
                <li key={key}>
                  <button
                    type="button"
                    aria-pressed={show === key}
                    onClick={() => setSearch({ show: key === 'all' ? undefined : key })}
                    className={cn(
                      'h-9 rounded-full border px-3.5 text-sm font-medium whitespace-nowrap',
                      show === key
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'bg-card hover:bg-secondary',
                    )}
                  >
                    {label}{' '}
                    <span
                      className={cn(
                        'tabular',
                        show === key ? 'text-primary-foreground/80' : 'text-muted-foreground',
                      )}
                    >
                      {count}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>
      )}

      {filtering && (
        <p
          className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
          aria-live="polite"
        >
          {rows.length} {rows.length === 1 ? 'quote matches' : 'quotes match'}
          <button
            type="button"
            className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            onClick={() => {
              setText('')
              setSearch({ q: undefined, owner: undefined })
            }}
          >
            <X className="size-3.5" aria-hidden />
            Clear search
          </button>
        </p>
      )}

      {isError ? (
        <p role="alert" className="text-destructive">
          {error.message}
        </p>
      ) : isPending ? (
        <div className="grid gap-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ) : all.length === 0 ? (
        <Empty className="rounded-xl border bg-card py-12">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileText />
            </EmptyMedia>
            <EmptyTitle>No quotes yet</EmptyTitle>
            <EmptyDescription>
              Start a quote for a new or existing customer. It takes a couple of minutes.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button className="h-10" onClick={() => setCreating(true)}>
              <Plus />
              New quote
            </Button>
          </EmptyContent>
        </Empty>
      ) : rows.length === 0 && view !== 'kanban' ? (
        <p className="rounded-xl border bg-card p-6 text-[15px] text-muted-foreground">
          {filtering
            ? 'Nothing matches your search. Try fewer or different words.'
            : 'No quotes in this stage.'}
        </p>
      ) : view === 'kanban' ? (
        <QuoteKanban rows={rows} />
      ) : view === 'cards' ? (
        <QuoteCards rows={rows} />
      ) : (
        <QuoteList rows={rows} />
      )}
      <NewQuoteDialog open={creating} onOpenChange={setCreating} />
    </>
  )
}
